import React, { FC, useCallback, useState } from 'react'
import { useParams } from 'react-router'
import { useHistory } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
// import { toast } from 'react-toastify'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'

import { ISfBankAccount } from '@sparling/financial-common'
import { Modal } from '../../../../components/Modal'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { Loader } from '../../../../components/Loader'
import { IDataItem, TableComponent } from '../../../../components/Table'
import { icons } from '../../../../constants/icons'
import { columns, modalColumns } from './BankAccountsList'
import { UserSparlingAccountModal } from '../UserSparlingAccountModal'
import {
  getAssignedAccountsByUser,
  assignedAccountsIsLoading,
  getSfBankAccountsData,
  sfBankAccountsIsLoading,
  getAssignedAccountIdsByUser,
} from '../../../../store/selectors'
import {
  assignSfBankAccountToUserRequest,
  getAssignedAccountsByUserRequest,
  getSfBankAccountsRequest,
  unassignSfBankAccountFromUserRequest,
} from '../../../../store/actions'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginTop: theme.spacing(2),
    padding: theme.spacing(4),
    height: '100%',
  },
  titleWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    minWidth: 400,
  },
  table: {
    paddingTop: theme.spacing(2),
  },
  buttonWrap: {
    justifyContent: 'flex-end',
  },
}))

export const BankAccounts: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const { idSf } = useParams<{ idSf: string }>()
  const dispatch = useDispatch()
  const isLoading = useSelector(assignedAccountsIsLoading)
  const assignedIds = useSelector(getAssignedAccountIdsByUser)
  const { items: assignedAccountsData, meta: params } = useSelector(getAssignedAccountsByUser)
  const isLoadingBankAccounts = useSelector(sfBankAccountsIsLoading)
  const { items: bankAccounts, meta } = useSelector(getSfBankAccountsData)
  const [isOpenAccountModal, setOpenAccountModal] = useState<boolean>(false)
  const [isOpenUnassignModal, setOpenUnassignModal] = useState<boolean>(false)
  const [idToUnassign, setIdToUnassign] = useState<number | null>(null)

  const loadSfBankAccounts = useCallback(
    (page: number) => {
      if (idSf) {
        dispatch(getSfBankAccountsRequest({ excludes: assignedIds, page, limit: 10 }))
      }
    },
    [dispatch, idSf, assignedIds],
  )

  const loadAssignedAccounts = useCallback(
    (page: number) => {
      if (idSf) {
        dispatch(getAssignedAccountsByUserRequest({ idSf, page, count: 10 }))
      }
    },
    [dispatch, idSf],
  )

  const unassignSfBankAccountFromUser = useCallback(() => {
    if (idToUnassign && idSf) {
      dispatch(unassignSfBankAccountFromUserRequest({ accountIds: [idToUnassign], userIdSf: idSf }))
      setOpenUnassignModal(false)
      setIdToUnassign(null)
    }
  }, [dispatch, idToUnassign, idSf])

  const openUnassignModalHandle = useCallback((id: number) => {
    setIdToUnassign(id)
    setOpenUnassignModal(true)
  }, [])

  const openBankAccountModalHandle = useCallback(() => {
    dispatch(getSfBankAccountsRequest({ excludes: assignedIds, page: 1, limit: 10 }))
    setOpenAccountModal(true)
  }, [dispatch, assignedIds])

  const closeBankAccountModalHandle = useCallback(() => setOpenAccountModal(false), [])

  const closeUnassignModalHandle = useCallback(() => setOpenUnassignModal(false), [])

  const infoHandle = useCallback(() => history.push('/finance/accounts/bank-accounts?page=1'), [history])

  const submitSelectedAccounts = useCallback(
    (accountIds: number[]) => {
      if (idSf) {
        dispatch(assignSfBankAccountToUserRequest({ accountIds, userIdSf: idSf }))
        closeBankAccountModalHandle()
      }
    },
    [dispatch, idSf, closeBankAccountModalHandle],
  )

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      <Grid container className={classes.container}>
        <Grid container className={classes.titleWrap}>
          <Typography variant={'h6'}>Bank Accounts</Typography>

          <GreenButton
            width={140}
            prefix={icons.ADD}
            ml={8}
            text={'Add Bank Account'}
            onClick={openBankAccountModalHandle}
          />
        </Grid>

        <Grid container className={classes.table}>
          <TableComponent
            columns={columns}
            data={assignedAccountsData as IDataItem<ISfBankAccount>[]}
            isShowRemoveIcon={true}
            isShowEditIcon={false}
            isShowInfoIcon={true}
            onInfo={infoHandle}
            onRemove={openUnassignModalHandle}
            loading={isLoading}
            loadData={loadAssignedAccounts}
            params={params}
          />
        </Grid>
      </Grid>
      {isOpenAccountModal && (
        <UserSparlingAccountModal
          isOpen={isOpenAccountModal}
          modalClose={closeBankAccountModalHandle}
          title={'Add Bank Account'}
          columns={modalColumns}
          data={bankAccounts as IDataItem<ISfBankAccount>[]}
          meta={meta}
          loading={isLoadingBankAccounts}
          loadingFunc={loadSfBankAccounts}
          assignHandle={submitSelectedAccounts}
          width={680}
        />
      )}
      {isOpenUnassignModal && (
        <Modal
          title={'Unassigning Bank Account'}
          isOpen={isOpenUnassignModal}
          modalClose={closeUnassignModalHandle}
          submitTitle={'Unassign'}
          onSubmit={unassignSfBankAccountFromUser}
        >
          Are you sure you would like to unassign a bank account: {idToUnassign}?
        </Modal>
      )}
    </>
  )
}
